import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Lightbulb, Users, Star, ArrowRight, CheckCircle, Globe, Smile, Shield, Award, BookOpen, GraduationCap } from 'lucide-react';
import PageHero from '../components/PageHero';
import { fadeUp, fadeLeft, fadeRight, stagger, viewportOpts } from '../hooks/useScrollAnimation';
import { siteConfig } from '../config/siteConfig';
import { leadership, teachers } from '../data/teachers';
import {
  img1, img2, img3, img4, img5, img6, img7, img8, img9, img10,
  img11, img12, img13, img14, img15, img16, img17
} from '../assets/images';

const Wave = ({ fill, bg = "transparent" }) => (
  <div className="wave-divider" style={{ background: bg }}>
    <svg viewBox="0 0 1440 70" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ height: 50 }}>
      <path d="M0,35 C200,70 400,0 720,35 C1040,70 1280,5 1440,35 L1440,70 L0,70 Z" fill={fill} />
    </svg>
  </div>
);

const values = [
  { icon: Heart, title: "Learning Through Play", desc: "We believe children learn best when they are engaged, curious, and having fun.", color: "#FDE8E9", iconColor: "#C8161D" },
  { icon: Users, title: "Respect for Every Child", desc: "Each child is unique and deserves to be seen, heard, and valued unconditionally.", color: "#F3F9E2", iconColor: "#9DC41E" },
  { icon: Smile, title: "Emotional Well-Being First", desc: "A child who feels safe and happy learns better. We prioritize emotional health above all.", color: "#E0F2FE", iconColor: "#0284c7" },
  { icon: Lightbulb, title: "Creativity & Innovation", desc: "We encourage children to think, imagine, create, and solve problems in their own unique ways.", color: "#FEF9C3", iconColor: "#ca8a04" },
  { icon: Shield, title: "Strong Moral Values", desc: "Building character, integrity, kindness, and respect alongside academic skills.", color: "#FCE7F3", iconColor: "#DB2777" },
];

const timeline = [
  { year: "2008", title: "Happy Kidz Founded", desc: "Founded under the vision of B.E.T., Happykidz Preschool was established to bring quality early education with global standards and local values." },
  { year: "2010", title: "ISO 9001:2008 Certified", desc: "Achieved ISO 9001:2008 certification, recognizing our commitment to quality education and continuous improvement." },
  { year: "2014", title: "Campus Expansion", desc: "Expanded to a larger, state-of-the-art facility with modern classrooms, art rooms, and outdoor play areas." },
  { year: "2018", title: "Award Recognition", desc: "Recognized for excellence in early childhood education at the Odisha Education Excellence Awards." },
  { year: "2023", title: "500+ Students Milestone", desc: "Celebrated over 500 enrolled students and 15 dedicated educators making a difference every single day." },
];

export default function About() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [hash]);

  return (
    <div>
      <PageHero title="About Us" subtitle="Learn about our story, mission, and the passionate people who make Happy Kidz special." breadcrumb="About Us" />

      {/* ISO + BET Badges */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6">
            {[
              { label: "Established 2008", sub: "B.E.T. Initiative", color: "#FDE8E9", icon: Award, ic: "#C8161D" },
              { label: "ISO 9001:2008", sub: "Certified Education Provider", color: "#F3F9E2", icon: Shield, ic: "#9DC41E" },
              { label: "500+ Students", sub: "Happy Families", color: "#E0F2FE", icon: Users, ic: "#0284c7" },
              { label: "Award Winning", sub: "Excellence in Education", color: "#FEF9C3", icon: Star, ic: "#ca8a04" },
            ].map((b, i) => (
              <motion.div key={i} className="flex items-center gap-3 px-6 py-3 rounded-2xl" style={{ backgroundColor: b.color }}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/60">
                  <b.icon size={20} style={{ color: b.ic }} />
                </div>
                <div>
                  <div className="font-nunito font-extrabold text-sm" style={{ color: b.ic }}>{b.label}</div>
                  <div className="text-gray-500 text-xs">{b.sub}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeLeft}>
              <span className="section-label">Our Story</span>
              <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3 mb-6">Where Learning Begins with Joy</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We believe childhood is a beautiful journey of discovery, imagination, and growth. Founded in 2008 under the vision of B.E.T., Happykidz Preschool was established to bring quality early education to young learners with global standards and local values.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                As a developmental learning center committed to holistic child growth, we offer a warm and engaging environment, child-centric teaching methods, and strong foundational learning — all designed to inspire a lifelong love for learning.
              </p>
              <div className="space-y-3">
                {[
                  "Nurturing confidence, curiosity, and compassion in every child",
                  "Strong communication between parents, teachers, and children",
                  "Hands-on, activity-based curriculum with personalized attention",
                  "Safe, child-friendly campus with experienced educators",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="text-[#9DC41E] flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-gray-600 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div className="grid grid-cols-2 gap-4" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeRight}>
              {[
                img1,
                img2,
                img3,
                img4,
              ].map((src, i) => (
                <div key={i} className={`rounded-2xl overflow-hidden shadow-md ${i === 0 ? 'col-span-2 h-52' : 'h-44'}`}>
                  <img src={src} alt="School life" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <Wave fill="#F8FAFC" bg="white" />
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Our Purpose</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Mission &amp; Vision</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div className="rounded-3xl p-10 border-2 border-[#C8161D]/10" style={{ background: 'linear-gradient(135deg, #FFF5F5, #FDE8E9)' }}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="w-14 h-14 bg-[#C8161D] rounded-2xl flex items-center justify-center mb-6">
                <Heart size={28} className="text-white" fill="white" />
              </div>
              <h3 className="font-nunito font-bold text-2xl text-[#1A1A1A] mb-4">Our Mission</h3>
              <ul className="space-y-3">
                {[
                  "To create a safe and nurturing learning environment",
                  "To build strong academic foundations",
                  "To encourage creativity and curiosity",
                  "To promote inclusivity and respect",
                  "To collaborate with families for overall development",
                ].map((m, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                    <CheckCircle size={16} className="text-[#C8161D] mt-0.5 flex-shrink-0" />
                    {m}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div className="rounded-3xl p-10 border-2 border-[#9DC41E]/10" style={{ background: 'linear-gradient(135deg, #F8FFF0, #F3F9E2)' }}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
              <div className="w-14 h-14 bg-[#9DC41E] rounded-2xl flex items-center justify-center mb-6">
                <Star size={28} className="text-white" fill="white" />
              </div>
              <h3 className="font-nunito font-bold text-2xl text-[#1A1A1A] mb-4">Our Vision</h3>
              <ul className="space-y-3">
                {[
                  "To be a leader in early childhood education",
                  "To build a joyful learning community",
                  "To inspire lifelong learners",
                  "To promote equality and diversity",
                ].map((v, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                    <CheckCircle size={16} className="text-[#9DC41E] mt-0.5 flex-shrink-0" />
                    {v}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
      <Wave fill="white" bg="#F8FAFC" />

      {/* Philosophy & Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">What We Stand For</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Our Philosophy &amp; Core Values</h2>
          </motion.div>
          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={stagger}>
            {values.map((v, i) => (
              <motion.div key={i} variants={fadeUp} className="cartoon-card bg-white rounded-3xl p-6 border border-gray-100 text-center">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: v.color }}>
                  <v.icon size={26} style={{ color: v.iconColor }} />
                </div>
                <h3 className="font-nunito font-bold text-base text-[#1A1A1A] mb-2">{v.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-10" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">School Life</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Life at Happy Kidz</h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              img5,
              img6,
              img7,
              img8,
              img9,
              img10,
              img11,
              img12,
            ].map((src, i) => (
              <motion.div key={i} className="rounded-2xl overflow-hidden h-44"
                initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}>
                <img src={src} alt="School life" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Our Journey</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Milestones Over the Years</h2>
          </motion.div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#C8161D]/20 -translate-x-1/2 hidden md:block" />
            {timeline.map((item, i) => (
              <motion.div key={i} className={`flex items-center gap-6 mb-10 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="flex-1">
                  {i % 2 === 0 ? (
                    <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 text-left">
                      <h4 className="font-nunito font-bold text-lg text-[#1A1A1A] mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-sm">{item.desc}</p>
                    </div>
                  ) : <div className="hidden md:block" />}
                </div>
                <div className="relative z-10 flex-shrink-0">
                  <div className="w-16 h-16 bg-[#C8161D] rounded-full flex items-center justify-center text-white font-nunito font-extrabold text-sm shadow-lg">{item.year}</div>
                </div>
                <div className="flex-1">
                  {i % 2 !== 0 ? (
                    <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
                      <h4 className="font-nunito font-bold text-lg text-[#1A1A1A] mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-sm">{item.desc}</p>
                    </div>
                  ) : <div className="hidden md:block" />}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Principal's Message */}
      <Wave fill="#FFFBF0" bg="white" />
      <section className="py-16 bg-[#FFFBF0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12 items-center">
            <motion.div className="lg:col-span-1 text-center" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
              <div className="w-52 h-52 rounded-full overflow-hidden mx-auto border-4 border-[#ca8a04]/20 shadow-xl mb-4">
                <img src={leadership[0].image} alt={leadership[0].name} className="w-full h-full object-cover" />
              </div>
              <h3 className="font-nunito font-extrabold text-2xl text-[#1A1A1A]">{leadership[0].name}</h3>
              <p className="text-[#ca8a04] font-bold text-sm">{leadership[0].role}</p>
              <p className="text-gray-500 text-xs mt-1">{leadership[0].qual}</p>
            </motion.div>
            <motion.div className="lg:col-span-2 bg-white rounded-3xl p-8 lg:p-10 shadow-md border border-gray-100 relative" initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="absolute -top-5 -left-2 text-6xl text-[#ca8a04]/10 font-serif">“</div>
              <h3 className="font-nunito font-extrabold text-2xl text-[#1A1A1A] mb-4">Principal's Welcome</h3>
              <p className="text-gray-600 leading-relaxed mb-4 text-sm md:text-base">
                "Welcome to {siteConfig.name}. We believe early childhood is the most crucial period for brain development, personality building, and nurturing natural curiosity. Our mission is to provide a safe, joyful, and inspiring space where children learn through play, exploration, and creative expression."
              </p>
              <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                "We work hand-in-hand with parents to ensure every child gets the personalized care and foundation they need to thrive. Thank you for trusting us with your child's educational journey."
              </p>
              <div className="mt-6 border-t border-gray-100 pt-4 flex justify-between items-center">
                <div>
                  <div className="font-nunito font-bold text-[#1A1A1A] text-sm">Anita Das</div>
                  <div className="text-gray-500 text-xs">Founder & Principal, Happykidz</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <Wave fill="white" bg="#FFFBF0" />

      {/* Our Team Section */}
      <section id="our-team" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
            <span className="section-label">Our Team</span>
            <h2 className="font-nunito font-extrabold text-4xl text-[#1A1A1A] mt-3">Passionate Educators & Leaders</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Meet the experienced leaders and caring teachers dedicated to your child's growth.</p>
          </motion.div>

          {/* Leadership Profiles */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {leadership.map((l, i) => (
              <motion.div
                key={i}
                className="bg-[#F8FAFC] rounded-3xl p-6 md:p-8 border border-gray-100 flex flex-col md:flex-row gap-6 items-center md:items-start"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="w-32 h-32 md:w-36 md:h-36 rounded-2xl overflow-hidden flex-shrink-0 shadow-md">
                  <img src={l.image} alt={l.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <span className="bg-[#C8161D]/10 text-[#C8161D] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{l.role}</span>
                  <h3 className="font-nunito font-extrabold text-xl text-[#1A1A1A] mt-2.5">{l.name}</h3>
                  <div className="flex flex-wrap justify-center md:justify-start gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                    <span className="font-semibold">{l.exp}</span>
                    <span>•</span>
                    <span>{l.qual}</span>
                  </div>
                  <p className="text-gray-600 text-xs md:text-sm mt-3 leading-relaxed">{l.bio}</p>
                  {l.fav && (
                    <div className="mt-3 bg-white/60 inline-block px-3 py-1.5 rounded-lg border border-gray-100 text-xs">
                      <span className="font-bold text-gray-500">Favorite book:</span> <span className="italic text-[#C8161D]">"{l.fav}"</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Teachers Grid */}
          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOpts}
            variants={stagger}
          >
            {teachers.map((t, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="cartoon-card rounded-3xl overflow-hidden bg-white border border-gray-100 group"
              >
                <div className="h-56 overflow-hidden relative">
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full text-[10px] font-bold text-gray-700 shadow-sm border border-gray-100">
                    {t.exp} exp
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-nunito font-extrabold text-lg text-[#1A1A1A]">{t.name}</h3>
                  <p className="text-[#9DC41E] font-semibold text-xs mt-0.5">{t.role}</p>
                  <p className="text-gray-400 text-[11px] mt-1">{t.qual}</p>
                  {t.fav && (
                    <div className="mt-3.5 pt-3 border-t border-gray-50 text-[11px] text-gray-500">
                      <span className="font-semibold">Favorite book:</span> <br/>
                      <span className="italic text-[#C8161D]">"{t.fav}"</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#C8161D] to-[#9B1015] text-white">
        <motion.div className="max-w-4xl mx-auto px-4 text-center" initial="hidden" whileInView="visible" viewport={viewportOpts} variants={fadeUp}>
          <h2 className="font-nunito font-extrabold text-4xl mb-4">Come Visit Us Today</h2>
          <p className="text-red-100 text-lg mb-8">Experience the Happy Kidz difference in person. We'd love to welcome your family!</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="bg-white text-[#C8161D] font-nunito font-bold rounded-full px-8 py-4 hover:bg-red-50 transition-all hover:-translate-y-1 inline-flex items-center gap-2 shadow-lg">Book a Visit <ArrowRight size={18} /></Link>
            <Link to="/admissions" className="btn-outline-white">Enroll Now</Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
